'use client';

/**
 * AstorWidgetNudge Component
 *
 * A card that slides in from the bubble position, displaying contextual nudges
 * with action buttons, auto-dismiss countdown, and a "Don't show again" option.
 *
 * @see docs/plans/2026-02-20-DESIGN-astor-ai-native-onboarding-help.md
 */

import CloseIcon from '@mui/icons-material/Close';
import { alpha, Box, IconButton, LinearProgress, Typography, useTheme } from '@mui/material';
import type { AstorNudge, NudgeAction } from '@ops-matrix/shared-types';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useI18n } from '../../hooks/useI18n';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { glassEffects } from '../../theme/shadows';
import { iconSizes } from '../../theme/typography';
import { GlassButton } from '../GlassButton';

// ============================================================================
// TYPES
// ============================================================================

export interface AstorWidgetNudgeProps {
  /** The nudge to display */
  nudge: AstorNudge;
  /** Callback when a nudge action button is clicked */
  onAction: (action: NudgeAction) => void;
  /** Callback when the nudge is dismissed (X button or auto-dismiss) */
  onDismiss: () => void;
  /** Callback for "Don't show again" link */
  onDontShowAgain: () => void;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const NUDGE_WIDTH = 320;
const DEFAULT_AUTO_DISMISS_SECONDS = 10;
const COUNTDOWN_INTERVAL_MS = 100;

// ============================================================================
// ANIMATION VARIANTS
// ============================================================================

const nudgeVariants = {
  hidden: { opacity: 0, y: 16, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.25, ease: [0.215, 0.61, 0.355, 1] },
  },
  exit: {
    opacity: 0,
    y: 8,
    scale: 0.98,
    transition: { duration: 0.15, ease: 'easeIn' },
  },
};

const reducedMotionVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.1 } },
  exit: { opacity: 0, transition: { duration: 0.1 } },
};

// ============================================================================
// HELPER: Map NudgeAction type to GlassButton variant
// ============================================================================

function getButtonVariant(actionType: NudgeAction['type']): 'subtle' | 'outlined' | 'gradient' {
  switch (actionType) {
    case 'open_chat':
      return 'gradient';
    case 'navigate':
    case 'start_tour':
      return 'outlined';
    case 'execute':
      return 'gradient';
    case 'dismiss':
    default:
      return 'subtle';
  }
}

// ============================================================================
// COMPONENT
// ============================================================================

export function AstorWidgetNudge({
  nudge,
  onAction,
  onDismiss,
  onDontShowAgain,
}: AstorWidgetNudgeProps): React.JSX.Element {
  const theme = useTheme();
  const { t } = useI18n();
  const prefersReducedMotion = useReducedMotion();
  const isDark = theme.palette.mode === 'dark';
  const glass = isDark ? glassEffects.dark : glassEffects.light;

  const autoDismissSeconds = nudge.autoDismissSeconds ?? DEFAULT_AUTO_DISMISS_SECONDS;
  const [progress, setProgress] = useState(100);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<number>(Date.now());

  // Auto-dismiss countdown
  useEffect(() => {
    if (autoDismissSeconds <= 0) return;

    startTimeRef.current = Date.now();
    const totalMs = autoDismissSeconds * 1000;

    timerRef.current = setInterval(() => {
      const elapsed = Date.now() - startTimeRef.current;
      const remaining = Math.max(0, 100 - (elapsed / totalMs) * 100);
      setProgress(remaining);

      if (remaining <= 0) {
        if (timerRef.current) clearInterval(timerRef.current);
        onDismiss();
      }
    }, COUNTDOWN_INTERVAL_MS);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [autoDismissSeconds, onDismiss]);

  // Pause countdown on hover
  const handleMouseEnter = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Resume countdown on mouse leave
  const handleMouseLeave = useCallback(() => {
    if (autoDismissSeconds <= 0 || progress <= 0) return;

    const remainingMs = (progress / 100) * autoDismissSeconds * 1000;
    startTimeRef.current = Date.now() - (autoDismissSeconds * 1000 - remainingMs);

    timerRef.current = setInterval(() => {
      const elapsed = Date.now() - startTimeRef.current;
      const totalMs = autoDismissSeconds * 1000;
      const remaining = Math.max(0, 100 - (elapsed / totalMs) * 100);
      setProgress(remaining);

      if (remaining <= 0) {
        if (timerRef.current) clearInterval(timerRef.current);
        onDismiss();
      }
    }, COUNTDOWN_INTERVAL_MS);
  }, [autoDismissSeconds, progress, onDismiss]);

  const variants = prefersReducedMotion ? reducedMotionVariants : nudgeVariants;

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={nudge.id}
        variants={variants}
        initial="hidden"
        animate="visible"
        exit="exit"
        style={{
          width: NUDGE_WIDTH,
          position: 'absolute',
          bottom: 68,
          right: 0,
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <Box
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            background: glass.background,
            backdropFilter: glass.backdropBlur,
            WebkitBackdropFilter: glass.backdropBlur,
            border: `1px solid ${glass.border}`,
            boxShadow: glass.shadow,
          }}
        >
          {/* Auto-dismiss progress bar */}
          {autoDismissSeconds > 0 && (
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 2,
                bgcolor: 'transparent',
                '& .MuiLinearProgress-bar': {
                  bgcolor: 'primary.main',
                  transition: 'none',
                },
              }}
            />
          )}

          {/* Header with close button */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              px: 2,
              pt: 1.5,
              pb: 0,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                flex: 1,
                pt: 0.5,
              }}
            >
              {nudge.title}
            </Typography>
            <IconButton
              size="small"
              onClick={onDismiss}
              aria-label={t('a11y.dismissNudge')}
              sx={{
                color: 'text.secondary',
                opacity: 0.7,
                '&:hover': { opacity: 1 },
              }}
            >
              <CloseIcon sx={{ fontSize: iconSizes.md }} />
            </IconButton>
          </Box>

          {/* Message */}
          <Box sx={{ px: 2, pb: 1.5 }}>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                lineHeight: 1.5,
              }}
            >
              {nudge.message}
            </Typography>
          </Box>

          {/* Action buttons */}
          {nudge.actions.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                px: 2,
                pb: 1.5,
                flexWrap: 'wrap',
              }}
            >
              {nudge.actions.map((action, index) => (
                <GlassButton
                  key={`${action.type}-${index}`}
                  variant={getButtonVariant(action.type)}
                  size="small"
                  onClick={() => onAction(action)}
                >
                  {action.label}
                </GlassButton>
              ))}
            </Box>
          )}

          {/* "Don't show again" link */}
          <Box
            sx={{
              px: 2,
              pb: 1.5,
              borderTop: `1px solid ${isDark ? alpha(theme.palette.common.white, 0.06) : alpha(theme.palette.common.black, 0.06)}`,
            }}
          >
            <Typography
              component="button"
              variant="caption"
              onClick={onDontShowAgain}
              sx={{
                color: 'text.secondary',
                opacity: 0.7,
                cursor: 'pointer',
                background: 'none',
                border: 'none',
                padding: 0,
                fontFamily: 'inherit',
                '&:hover': {
                  opacity: 1,
                  textDecoration: 'underline',
                },
              }}
            >
              {t('astor.nudge.dontShowAgain')}
            </Typography>
          </Box>
        </Box>
      </motion.div>
    </AnimatePresence>
  );
}

AstorWidgetNudge.displayName = 'AstorWidgetNudge';

export default AstorWidgetNudge;
